import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomForm } from '../../hooks/useCustomForm';
import { UserPermission } from '../../types/userPermission';
import { User } from '../../types/users';
import { Permission } from '../../types/permission';
import { checkIfUserPermissionExists } from '../../services/Administracion/userPermissionService';

interface UserPermissionModalProps {
    userPermission: UserPermission | null;
    users: User[];
    permissions: Permission[];
    userPermissions: UserPermission[];    
    onClose: () => void;
    onSave: (userPermission: UserPermission) => void;
}

const UserPermissionModal: React.FC<UserPermissionModalProps> = ({
    userPermission,
    users,
    permissions,
    userPermissions,
    onClose,
    onSave
}) => {

    const defaultValues = useMemo<UserPermission>(() => {
        return userPermission || {
            user_id: 0,
            permission_id: 0,
            is_active: true,
        };
    }, [userPermission]);

    const [userPermissionExists, setUserPermissionExists] = useState<boolean>(false);

    const onSubmit = (data: UserPermission) => {
        const updated = userPermission ? { ...userPermission, ...data } : data;
        onSave(updated);
        console.log('Data en onSubmit: ', data);
    };

    const { register, errors, isValid, submitHandler, reset, watch } = useCustomForm<UserPermission>({
        defaultValues,
        onSubmit,
    });

    const watchedUserId = watch('user_id');
    const watchedPermissionId = watch('permission_id');

    const checkIfExists = useCallback(async (user_id: number, permission_id: number) => {
        if (user_id && permission_id && !userPermission) {
            try {
                const exists = await checkIfUserPermissionExists(user_id, permission_id);
                setUserPermissionExists(exists);
            } catch (error) {
                console.error('Error al verificar combinación:', error);
            }
        }
    }, [userPermission]);

    const assignedPermissions = useMemo(() => {
        return userPermissions
            .filter(up => up.user_id === watchedUserId)
            .map(up => {
                const permission = permissions.find(p => p.permission_id === up.permission_id);
                return permission ? permission : null;
            })
            .filter((p): p is Permission => p !== null);
    }, [userPermissions, permissions, watchedUserId]); // Filter null values

    useEffect(() => {
        if (!userPermission && watchedUserId && watchedPermissionId) {
            checkIfExists(watchedUserId, watchedPermissionId);
        }
    }, [watchedUserId, watchedPermissionId, userPermission, checkIfExists]);
 
    useEffect(() => {
        reset(defaultValues);
    }, [userPermission, reset, defaultValues]);

    return (
        <div className="modal show d-block" tabIndex={-1} role='dialog'>
            <div className="modal-dialog modal-dialog-centered" role='document'>
                <div className="modal-content shadow-lg bg-light">
                    <div className="modal-header">
                        <h5 className='modal-title'>Asignar Permiso a Usuario</h5>
                        <button type='button' className='btn-close' onClick={onClose}></button>
                    </div>
                    <form onSubmit={submitHandler}>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label htmlFor="user_id" className='form-label'>Usuario</label>
                                <select
                                    id="user_id"
                                    className={`form-select ${errors.user_id ? 'is-invalid' : ''}`}
                                    disabled={!!userPermission}
                                    {...register('user_id', { required: 'El usuario es requerido', valueAsNumber: true })}
                                >
                                    <option value="">Seleccione un usuario</option>
                                    {users.map(user => (
                                        user.is_active && (
                                            <option key={user.user_id} value={user.user_id}>
                                                {user.ad_username} 
                                            </option>
                                        )
                                    ))}
                                </select>
                                {userPermission && (
                                    <input type="hidden" value={userPermission.user_id} {...register('user_id')} />
                                )}
                                {errors.user_id && (
                                    <div className="invalid-feedback">{errors.user_id.message}</div>
                                )}
                            </div>
                            {/* Permisos ya asignados */}
                            {assignedPermissions.length > 0 && (
                                <div className="alert alert-info p-2">
                                    <strong>
                                        {`Permisos ya asignados a ${userPermission
                                            ? users.find(u => u.user_id === userPermission?.user_id)?.ad_username
                                            : users.find(u => u.user_id === watchedUserId)?.ad_username || 'Usuario no encontrado'
                                            }
                                        :`}
                                    </strong>
                                    {assignedPermissions.map(p => p.permission_name).join(', ')}
                                </div>
                            )}
                            <div className="mb-3">
                                <label htmlFor="permission_id" className='form-label'>Permiso</label>
                                <select
                                    id="permission_id"
                                    className={`form-select ${errors.permission_id ? 'is-invalid' : ''}`}
                                    disabled={!!userPermission}
                                    {...register('permission_id', { required: 'El Permiso es requerido', valueAsNumber: true })}
                                >
                                    <option value="">Seleccione un permiso</option>
                                    {permissions.filter(p => {
                                        if (!userPermission) {
                                            return p.is_active && !assignedPermissions.some(ap => ap.permission_id === p.permission_id);
                                        } else {
                                            return p.is_active;
                                        }
                                    })
                                        .map(p => (
                                            <option key={p.permission_id} value={p.permission_id}>
                                                {p.permission_name}
                                            </option>
                                        ))};
                                </select>
                                {userPermission && (
                                    <input type="hidden" value={userPermission.permission_id} {...register('permission_id')} />
                                )}
                                {userPermissionExists && (
                                    <div className="ivalid-alert alert-warning mt-2">
                                        El permiso ya está asignado a este usuario
                                    </div>
                                )}
                            </div>
                            <div className="form-check">
                                <input
                                    id='is_active'
                                    type="checkbox"
                                    className="form-check-input"
                                    {...register('is_active')}
                                />
                                <label htmlFor="is_active" className="form-check-label">Activo</label>
                            </div>
                            <div className="modal-footer">
                                <button type='button' className="btn btn-secondary" onClick={onClose}>
                                    Cancelar
                                </button>
                                <button
                                    type='submit'
                                    className="btn btn-primary"
                                    disabled={!isValid || userPermissionExists}
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserPermissionModal;